angular.module('rjmNg.services').factory 'columnService',[
  "Columns"
  "$q"
  "app"
  "stringService"
  "dataWarehouse"
  (Columns, $q, app, stringService, dataWarehouse) ->
    columnService = {}

    ### Private ###

    columnService._getNativeColumnStatus = (column) ->
      if column.isTracked and column.is_in_serving_layer
        return "Synced"
      else if column.isTracked
        return "Pending"
      else
        return "Unsynced"

    columnService._getCalculatedColumnStatus = (column) ->
      if column.is_in_serving_layer
        return "Synced"
      else if app.hasFeatureFlag('RJQL_SERVICE_CLIENT')
        return "Synced"
      else
        return "Pending"

    columnService._tableIsLocal = (columns) ->
      for column in columns when column.id
        return true
      return false

    ### Public ###

    # Returns the column status
    columnService.getStatus = (column) ->
      if column.isDerivative
        return columnService._getCalculatedColumnStatus(column)
      else
        return columnService._getNativeColumnStatus(column)

    columnService.canSetName = (column) ->
      return columnService.getStatus(column) in ["Synced", "Pending"]

    # Returns the style
    columnService.getStyle = (column) ->
      status = columnService.getStatus(column)
      styleMap =
        Synced: 'success rj-icon--check-circle-o'
        Pending: 'waiting rj-icon--ellipsis-h'
        Unsynced: 'muted rj-icon--ban'

      return styleMap[status]

    columnService.getHelp = (column) ->
      status = columnService.getStatus(column)
      if column.isDerivative
        return _getCalculatedHelp(status)
      else
        return _getNativeHelp(status)

    _getNativeHelp = (status) ->
      helpMap =
        Synced: '<strong>Synced</strong>:
          This column is being synced to your data warehouse.'
        Pending: '<strong>Pending</strong>:
          This column will be added to your data warehouse the next time we sync.'
        Unsynced: '<strong>Unsynced</strong>:
          This column is not being synced to your data warehouse.'
      return helpMap[status]

    _getCalculatedHelp = (status) ->
      helpMap =
        Synced: '<strong>Synced</strong>:
          This column has been calculated and is avaialble to use.'
        Pending: '<strong>Pending</strong>:
          This column is being calculated and should be ready soon.'
      return helpMap[status]

    # Logic to determine tbe bulk selector state
    columnService.getBulkSelectState = (selected, unselected) ->
      state = 'error'
      if selected > 0 and unselected > 0
        state = 'some'
      else if selected is 0 and unselected > 0
        state = 'none'
      else if selected > 0 and unselected is 0
        state = 'all'

      return state

    # Check the columns and return its selection state
    columnService.parseSelections = (columns) ->

      result =
        columnsToAdd: 0
        columnsToRemove: 0
        selected: 0
        unselected: 0
        nativeSelected: 0

      isLocal = columnService._tableIsLocal columns
      for column in columns
        if column.selected
          result.selected += 1
          if column.isTracked
            unless column.isKey
              result.columnsToRemove += 1
            unless column.isDerivative
              result.nativeSelected += 1
          else
            result.columnsToAdd +=1
        else
          result.unselected += 1

      return result

    # Update columns based on an updated column
    columnService.updateSelections = (updatedColumn, columns) ->
      unless columnService._tableIsLocal columns
        if (updatedColumn.isKey and updatedColumn.selected) or updatedColumn.selected
          column.selected = true for column in columns when column.isKey
        else if updatedColumn.isKey
          column.selected = false for column in columns

    # Set recheck frequencies for an array of columns
    columnService.setRecheckFrequencies = (columns, freq) ->
      promises = []
      mainDeferred = $q.defer()

      for col in columns
        deferred = $q.defer()
        promises.push @setRecheckFrequency(col, freq, deferred)

      $q.all(promises).then( (success) ->
        mainDeferred.resolve(success)
      , (error) ->
        mainDeferred.reject(error)
      )

      return mainDeferred.promise

    columnService.setPks = (columns, status) ->
      promises = []
      mainDeferred = $q.defer()
      for col in columns
        deferred = $q.defer()
        promises.push @setPk(col, status, deferred)
      promises.push

      $q.all(promises).then( (success) ->
        mainDeferred.resolve(success)
      , (error) ->
        mainDeferred.reject(error)
      )

      return mainDeferred.promise

    columnService.setFieldTypes = (columns, ftype) ->
      promises = []
      mainDeferred = $q.defer()
      for col in columns
        deferred = $q.defer()
        promises.push @setFieldType(col, ftype, deferred)
      promises.push

      $q.all(promises).then( (success) ->
        mainDeferred.resolve(success)
      , (error) ->
        mainDeferred.reject(error)
      )

      return mainDeferred.promise

    # Set recheck frequency for a single column
    columnService.setRecheckFrequency = (column, freq, deferred) ->
      Columns.get(column.id).then( (col) ->
        col.recheck = freq
        oldFreq = column.recheck
        column.recheck = freq
        Columns.save(col).then( (savedColumn) =>
          deferred.resolve(savedColumn)
        , (error) ->
          column.recheck = oldFreq
          deferred.reject(error)
        )
      , (error) ->
        deferred.reject(error)
      )

      return deferred.promise

    columnService.setPk = (column, value, deferred) ->
      Columns.get(column.id).then( (col) ->
        col.primary_key_index = value
        oldValue = column.primary_key_index
        column.primary_key_index = value
        column.isKey = if value is 1 then true else false
        Columns.save(col).then( (savedColumn) =>
          deferred.resolve(savedColumn)
        , (error) ->
          column.primary_key_index = oldValue
          column.isKey = if oldValue is 1 then true else false
          deferred.reject(error)
        )
      , (error) ->
        deferred.reject(error)
      )

      return deferred.promise

    columnService.setFieldType = (column, value, deferred) ->
      Columns.get(column.id).then( (col) ->
        col = _prepareColumnForPUT(col)
        col.field_type = value
        oldValue = column.fieldType
        column.fieldType = value
        Columns.save(col).then( (savedColumn) =>
          deferred.resolve(savedColumn)
        , (error) ->
          column.fieldType = oldValue
          deferred.reject(error)
        )
      , (error) ->
        deferred.reject(error)
      )

      return deferred.promise

    _setNames = (column, name, key) ->
      column.name = name
      column[key] = name
      return

    columnService.setName = (column, key, tableId) ->
      newName = column[key]
      oldName = column.name
      deferred = $q.defer()
      Columns.query(tid: tableId, state: ["active"])
      .then(
        (columns) ->
          if _.some(columns, (c) -> c.id isnt column.id and c.name.toLowerCase() is newName.toLowerCase())
            _setNames(column, oldName, key)
            deferred.reject("name_already_exists")
          else
            col = _.find(columns, (c) -> c.id is column.id)

            col = _prepareColumnForPUT(col)
            col.name = newName

            Columns.save(col).then(
              (savedColumn) =>
                deferred.resolve(savedColumn)
                _setNames(column, newName, key)
              , (error) ->
                _setNames(column, oldName, key)
                deferred.reject(error)
            )
        , (error) ->
          _setNames(column, oldName, key)
          deferred.reject(error)
      )

      return deferred.promise

    # Are unable to set recheck frequency on any of the selected columns?
    columnService.hasInvalidRecheckSelections = (columns) ->
      for col in columns when @_isInvalidRecheckSelection(col)
        return true
      return false

    # Are unable to set recheck frequency on any of the selected columns?
    columnService.hasAnyValidRecheckSelections = (columns) ->
      unless columns?
        return false
      for col in columns when @_isValidRecheckSelection(col)
        return true
      return false

    _prepareColumnForPUT = (column) ->
      type = column?.type
      derivativeType = column?.properties?.derivative_type
      joins = column?.properties?.joins
      filter = column?.properties?.filter_set
      relocatedPathColumn = type is "derivative" and derivativeType is "relocated_field"
      if _.isArray(joins) and relocatedPathColumn
        column.properties.joins = _.map(joins, (j) ->
          _.pick(j, 'foreign_column_ids', 'local_column_ids')
        )
      if !_.isObject(filter) and relocatedPathColumn
        column.properties.filter_set =
          filters: []
          sequence: ""
      column

    columnService._isSelected = (col) ->
      return col.selected? and col.selected

    columnService._isValidRecheckSelection = (col) ->
      return @_isSelected(col) and col.isTracked and not col.isDerivative

    columnService._isInvalidRecheckSelection = (col) ->
      return @_isSelected(col) and (not col.isTracked or col.isDerivative)

    # Returns an array of all the selected native columns
    columnService.getSelectedNativeColumns = (columns) ->
      return (col for col in columns \
        when col.selected? and col.selected and not col.isDerivative and col.isTracked)

    # What should we display to the user for a recheck value?
    columnService.getDisplayedRecheckValue = (column, replicationMethod) ->
      if column.isDerivative
        return ''
      else if column.isView
        return 'Never'
      else if @_doRechecksMatterForRepMethod(replicationMethod)
        return stringService.upperCaseFirst(column.recheck)
      else if @_isDynamicReplicationMethod(replicationMethod)
        return 'Dynamic'
      else if @_isAppendOnlyReplicationMethod(replicationMethod)
        return 'Never'
      else if @_isFullTableReplication(replicationMethod)
        return 'Always'
      else if replicationMethod is dataWarehouse.REPLICATION_METHOD.NONE
        return 'Paused'
      else if replicationMethod is dataWarehouse.REPLICATION_METHOD.USERPUSH
        return "Never"
      else
        return ''

    # What helpful hover text should we display for this column?
    columnService.getRecheckHoverText = (column, replicationMethod) ->
      if column.isDerivative
        return ''
      else if @_doRechecksMatterForRepMethod(replicationMethod)
        return 'How often we check this column for changed values'
      else if @_isDynamicReplicationMethod(replicationMethod)
        return "This table's replication method ensures we can detect changes automatically"
      else if @_isAppendOnlyReplicationMethod(replicationMethod)
        return "This table uses a replication method that assumes previously replicated data doesn\'t change"
      else if @_isFullTableReplication(replicationMethod)
        return 'We replicate the entire table every time'
      else if replicationMethod is dataWarehouse.REPLICATION_METHOD.NONE
        return "We are not currently syncing this table's data"
      else if replicationMethod is dataWarehouse.REPLICATION_METHOD.USERPUSH
        return "This data came from a file upload and doesn't need to be rechecked."
      else
        return ''

    columnService.allowRechecks = (repMethod, cols) ->
      @_doRechecksMatterForRepMethod(repMethod) and @hasAnyValidRecheckSelections(cols)

    columnService.getRecheckButtonText = (replicationMethod, columns) ->
      if @allowRechecks(replicationMethod, columns)
        return "Set the recheck frequency for the columns you have selected."
      else
        return "The replication method for this table makes rechecks unecessary."

    # Some replication methods need rechecks, some don't
    columnService._doRechecksMatterForRepMethod = (replicationMethod) ->
      replicationMethodsNeedingRechecks = [
        dataWarehouse.REPLICATION_METHOD.SINGLE_AUTO_INC_PK,
        dataWarehouse.REPLICATION_METHOD.PK_BATCH,
        dataWarehouse.REPLICATION_METHOD.MONGO_ID,
        dataWarehouse.REPLICATION_METHOD.MONGO_BATCH,
        dataWarehouse.REPLICATION_METHOD.MONGO_SUBTABLE
      ]
      return replicationMethod in replicationMethodsNeedingRechecks

    columnService._isDynamicReplicationMethod = (replicationMethod) ->
      dynamicReplicationMethods = [
        dataWarehouse.REPLICATION_METHOD.MODIFIED_AT,
        dataWarehouse.REPLICATION_METHOD.MONGO_MODIFIED_AT,
        dataWarehouse.REPLICATION_METHOD.INCREMENTAL_REDSHIFT_UNLOAD
      ]
      return replicationMethod in dynamicReplicationMethods

    columnService._isAppendOnlyReplicationMethod = (replicationMethod) ->
      appendOnlyReplicationMethods = [
        dataWarehouse.REPLICATION_METHOD.ADD_DATE,
        dataWarehouse.REPLICATION_METHOD.UNIX_TS
      ]
      return replicationMethod in appendOnlyReplicationMethods

    columnService._isFullTableReplication = (replicationMethod) ->
      fullReplicationMethods = [
        dataWarehouse.REPLICATION_METHOD.FULL_TABLE,
        dataWarehouse.REPLICATION_METHOD.REDSHIFT_UNLOAD
      ]
      return replicationMethod in fullReplicationMethods

    columnService
]
